// PKGV.A3 (#794) — filter rail + label search + canvas, mounted inside
// PKGNeighbourhoodPanel's `ready` branch.
//
// V1 (locked): this component is the "keyed child" — the parent panel
// renders it with `key={currentAnchorId}`, so React remounts it fresh on
// every re-anchor. That remount IS the reset mechanism for L6 ("reset
// filters on re-anchor") — no manual clearing logic needed, it's
// structural, not vigilant. The fetched neighbourhood `data` stays in the
// PARENT panel (PKGNeighbourhoodPanel.tsx); this component only ever
// receives the already-fetched nodes/edges as props, never fetches.
//
// V3 (locked): URL-persistence is scoped to filter state on an
// ALREADY-EXPANDED panel — restoring which row is expanded from a cold
// URL needs host-page `expandedId` routing, filed as a follow-on
// (#1899), not this component's job.
//
// IMPORTANT — restoreFromUrl, and why this ISN'T just "read searchParams
// on mount": an earlier version read live `useSearchParams()` in this
// component's own lazy useState initializer, relying on the parent
// clearing the URL (synchronously, in the same handler that changes
// `key`) before this component's fresh mount ran. That's not actually
// reliable — react-router's `setSearchParams` goes through the router's
// own navigation mechanism, which is NOT guaranteed to commit in the
// same synchronous batch as a plain useState setter, even when both are
// called in the same handler (observed directly: a just-unchecked
// filter survived into the "reset" remount because this component's
// initializer ran before the router had actually applied the clear).
// The fix: the PARENT decides, as a plain boolean prop computed
// synchronously from its own state (`isReanchored`), whether this mount
// should consult the URL at all. Re-anchored mounts get `restoreFromUrl
// = false` and always initialize to pure defaults — no race, because
// no cross-hook timing is involved. Only the very first mount (viewing
// the original anchor, never re-anchored) reads real search params.

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PKGCanvas } from './PKGCanvas.tsx';
import type { PKGNode, PKGEdge, LayoutHint, InteractionLevel } from '@blueprint/shared';
import {
  filterSubgraph, presentNodeTypes, presentEdgeTypes, typeCounts, edgeTypeCounts,
} from './pkgFilter.ts';
import { PKGTypeEdgeFilterControl } from './PKGTypeEdgeFilterControl.tsx';
import { PKGLabelSearchCombobox } from './PKGLabelSearchCombobox.tsx';
import { readFilterStateFromUrl, writeFilterStateToUrl, type PanelFilterState } from './pkgFilterUrlState.ts';

export interface PKGFilterableNeighbourhoodProps {
  nodes: PKGNode[];
  edges: PKGEdge[];
  layoutHint: LayoutHint;
  interactionLevel: InteractionLevel;
  centerNodeId: string | undefined;
  onNodeClick: (node: PKGNode) => void;
  /** False on a re-anchored mount — see the file header note above. */
  restoreFromUrl: boolean;
}

export function PKGFilterableNeighbourhood({
  nodes,
  edges,
  layoutHint,
  interactionLevel,
  centerNodeId,
  onNodeClick,
  restoreFromUrl,
}: PKGFilterableNeighbourhoodProps) {
  const presentTypes = useMemo(() => presentNodeTypes(nodes), [nodes]);
  const presentEdgeTypesList = useMemo(() => presentEdgeTypes(edges), [edges]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Lazy initializer: read once, on this mount only (per-anchor, per V1's
  // keying). Intentionally NOT re-run on presentTypes/presentEdgeTypesList
  // changing identity across renders of the SAME mount.
  const [filterState, setFilterState] = useState<PanelFilterState>(() =>
    restoreFromUrl
      ? readFilterStateFromUrl(searchParams, presentTypes, presentEdgeTypesList)
      : { visibleTypes: new Set(presentTypes), visibleEdgeTypes: new Set(presentEdgeTypesList), searchQuery: '' },
  );
  const [searchQuery, setSearchQueryState] = useState(filterState.searchQuery);
  const [searchCenterOverride, setSearchCenterOverride] = useState<string | undefined>(undefined);

  const setVisibleTypes = useCallback((next: Set<typeof presentTypes[number]>) => {
    setFilterState(s => ({ ...s, visibleTypes: next }));
  }, []);
  const setVisibleEdgeTypes = useCallback((next: Set<typeof presentEdgeTypesList[number]>) => {
    setFilterState(s => ({ ...s, visibleEdgeTypes: next }));
  }, []);

  // Sync to URL on every change (S3/L7: default-equivalent state clears
  // params rather than writing them redundantly — see pkgFilterUrlState).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    writeFilterStateToUrl(next, { ...filterState, searchQuery }, presentTypes, presentEdgeTypesList);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams/setSearchParams intentionally excluded: including them would re-run this on every navigation, not just on filter/search changes.
  }, [filterState, searchQuery, presentTypes, presentEdgeTypesList]);

  const handleToggleType = (type: typeof presentTypes[number]) => {
    const next = new Set(filterState.visibleTypes);
    if (next.has(type)) next.delete(type); else next.add(type);
    setVisibleTypes(next);
  };
  const handleToggleEdgeType = (type: typeof presentEdgeTypesList[number]) => {
    const next = new Set(filterState.visibleEdgeTypes);
    if (next.has(type)) next.delete(type); else next.add(type);
    setVisibleEdgeTypes(next);
  };
  const handleResetFilters = () => {
    setVisibleTypes(new Set(presentTypes));
    setVisibleEdgeTypes(new Set(presentEdgeTypesList));
  };

  const filtered = useMemo(
    () => filterSubgraph(nodes, edges, filterState.visibleTypes, filterState.visibleEdgeTypes),
    [nodes, edges, filterState.visibleTypes, filterState.visibleEdgeTypes],
  );

  const handleSearchSelect = (node: PKGNode) => {
    setSearchCenterOverride(node.id);
  };

  // R1 adversarial-pass finding (MEDIUM): searchCenterOverride is never
  // explicitly cleared when a later filter toggle removes that node from
  // `filtered.nodes` — repro was "search -> select a result -> uncheck
  // that result's entity type." Without this membership check,
  // PKGCanvas's centerNodeId would reference a node id absent from the
  // nodes array it was also given, and radial.ts's layout (`const center
  // = centerNodeId ?? sortedIds[0]`) has no membership guard of its own,
  // silently degrading the layout instead of centering on anything real.
  // A derived membership check here is simpler and more robust than an
  // effect that has to remember to clear the override on every filter
  // path — it can't drift out of sync with `filtered.nodes` because it's
  // computed from it directly, every render.
  const effectiveCenterNodeId =
    (searchCenterOverride && filtered.nodes.some(n => n.id === searchCenterOverride))
      ? searchCenterOverride
      : centerNodeId;

  return (
    <div style={{ display: 'flex', gap: 16 }} data-testid="pkg-filterable-neighbourhood">
      <PKGTypeEdgeFilterControl
        typeCounts={typeCounts(nodes)}
        edgeTypeCounts={edgeTypeCounts(edges)}
        visibleTypes={filterState.visibleTypes}
        visibleEdgeTypes={filterState.visibleEdgeTypes}
        onToggleType={handleToggleType}
        onToggleEdgeType={handleToggleEdgeType}
        onReset={handleResetFilters}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <PKGLabelSearchCombobox
          nodes={filtered.nodes}
          value={searchQuery}
          onChange={setSearchQueryState}
          onSelect={handleSearchSelect}
        />

        {filtered.nodes.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No nodes match the current filters.</p>
        ) : (
          <div style={{ height: 360 }}>
            <PKGCanvas
              nodes={filtered.nodes}
              edges={filtered.edges}
              layoutHint={layoutHint}
              interactionLevel={interactionLevel}
              centerNodeId={effectiveCenterNodeId}
              onNodeClick={onNodeClick}
            />
          </div>
        )}
      </div>
    </div>
  );
}
